import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import {
  FEATURE_FLAG_DEFINITIONS,
  type FeatureFlagKey,
} from "@/lib/feature-flags";
import {
  useFeatureFlags,
  useUpsertFeatureFlag,
} from "@/hooks/use-feature-flags";

interface WorkspaceOption {
  id: string;
  name: string;
  status: string;
}

export default function AdminFeatureFlags() {
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(
    null,
  );

  const workspacesQuery = useQuery({
    queryKey: ["admin_workspaces"],
    queryFn: async (): Promise<WorkspaceOption[]> => {
      const { data, error } = await supabase
        .from("workspaces")
        .select("id, name, status")
        .order("name");

      if (error) throw error;
      return (data ?? []) as WorkspaceOption[];
    },
  });

  useEffect(() => {
    if (
      selectedWorkspaceId === null &&
      workspacesQuery.data &&
      workspacesQuery.data.length > 0
    ) {
      setSelectedWorkspaceId(workspacesQuery.data[0].id);
    }
  }, [selectedWorkspaceId, workspacesQuery.data]);

  const flags = useFeatureFlags({ workspaceId: selectedWorkspaceId });
  const upsert = useUpsertFeatureFlag();

  const handleToggle = (key: FeatureFlagKey, enabled: boolean) => {
    if (!selectedWorkspaceId) return;
    upsert.mutate({ workspaceId: selectedWorkspaceId, key, enabled });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-foreground">
          Feature Flags
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Enable or disable modules per workspace. Members only see enabled
          areas; missing flags default to off.
        </p>
      </div>

      <Card className="shadow-soft">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Workspace</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Label htmlFor="workspace-select">Select workspace</Label>
          {workspacesQuery.isLoading ? (
            <Skeleton className="h-10 w-full max-w-md" />
          ) : workspacesQuery.error ? (
            <p className="text-sm text-destructive">
              {workspacesQuery.error.message}
            </p>
          ) : !workspacesQuery.data?.length ? (
            <p className="text-sm text-muted-foreground">No workspaces found.</p>
          ) : (
            <Select
              value={selectedWorkspaceId ?? undefined}
              onValueChange={setSelectedWorkspaceId}
            >
              <SelectTrigger id="workspace-select" className="max-w-md">
                <SelectValue placeholder="Choose workspace" />
              </SelectTrigger>
              <SelectContent>
                {workspacesQuery.data.map((ws) => (
                  <SelectItem key={ws.id} value={ws.id}>
                    {ws.name}
                    {ws.status !== "active" ? ` (${ws.status})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </CardContent>
      </Card>

      <Card className="shadow-soft">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Module flags</CardTitle>
        </CardHeader>
        <CardContent>
          {!selectedWorkspaceId ? (
            <p className="text-sm text-muted-foreground">
              Select a workspace to manage flags.
            </p>
          ) : flags.error ? (
            <p className="text-sm text-destructive">{flags.error.message}</p>
          ) : flags.isLoading ? (
            <div className="space-y-4">
              {FEATURE_FLAG_DEFINITIONS.map((def) => (
                <Skeleton key={def.key} className="h-20 w-full" />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {FEATURE_FLAG_DEFINITIONS.map((def) => {
                const enabled = flags.isEnabled(def.key);
                return (
                  <div
                    key={def.key}
                    className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between p-4 rounded-lg border border-border"
                  >
                    <div>
                      <p className="font-medium">{def.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {def.description}
                      </p>
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                      <Badge variant={enabled ? "default" : "secondary"}>
                        {enabled ? "Enabled" : "Disabled"}
                      </Badge>
                      <Switch
                        checked={enabled}
                        disabled={upsert.isPending}
                        onCheckedChange={(checked) =>
                          handleToggle(def.key, checked)
                        }
                        aria-label={`Toggle ${def.name}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
